/**
 * PORTERS Connect API 集成 — 前端 API 客户端
 *
 * 配套后端: mygpt/endpoints/porters_admin.py + mygpt/oauth/porters_oauth.py
 *
 * 设计要点:
 *   - 跟 messaging 平台 (LINE / WowTalk / LineWorks) 一样, 每 bot 一条配置
 *   - Secret 以明文回显, 方便排查 (后端 GET 也返回明文)
 *   - 连接到 PORTERS 走浏览器 OAuth, 调用方在新标签页打开 authorize URL
 */

import { Http } from '@mygpt/app/http';

// ─── 类型定义 ───────────────────────────────────────────────────────────

export interface PortersIntegrationConfig {
  porters_company_id: string;
  app_id: string;
  secret: string;
  api_host?: string;
  scopes?: string;
  line_userid_field_alias?: string;
}

export interface PortersConfigDetail {
  bot_id: string;
  porters_company_id: string;
  app_id: string;
  secret: string;
  app_id_masked: string;
  api_host: string;
  scopes: string;
  line_userid_field_alias: string | null;
  partition_id: number | null;
  status: 'pending' | 'active' | 'error' | 'disabled';
  last_error: string | null;
  activated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PortersStatus {
  bot_id: string;
  status: 'pending' | 'active' | 'error' | 'disabled';
  has_token: boolean;
  access_expires_in_seconds: number | null;
  refresh_expires_in_seconds: number | null;
  last_refreshed_at: string | null;
  refresh_failure_count: number;
  last_error: string | null;
}

export interface PortersTestResult {
  bot_id: string;
  partitions?: Record<string, unknown>[];
  partition_count?: number;
  user_sample_total?: number;
  user_sample_first?: Record<string, unknown>[];
  error_at?: string;
  error?: string;
  error_code?: string;
}

export interface PortersApiLog {
  id: string;
  operation: string;
  method: string;
  url: string;
  request_body_redacted: string | null;
  response_status: number | null;
  response_body_raw: string | null;
  error_code: string | null;
  error_message: string | null;
  duration_ms: number | null;
  created_at: string;
}

// ─── API 调用 ───────────────────────────────────────────────────────────

/** 获取 PORTERS 配置详情 (未配置时后端返回 200 + null)
 *  跳过全局错误弹窗: 403 时不能让全局拦截器把整个设置页打到 /no-access, 由调用方兜底 */
export const getPortersConfig = async (botId: string): Promise<PortersConfigDetail | null> => {
  const { get } = Http.use();
  const response = await get<PortersConfigDetail | null>(`/api/bots/${botId}/porters/config`, {
    skipErrorNotification: true,
  });
  return response.data;
};

/** 新建或更新 PORTERS 配置 */
export const savePortersConfig = async (
  botId: string,
  config: PortersIntegrationConfig
): Promise<PortersConfigDetail> => {
  const { post } = Http.use();
  const response = await post<PortersConfigDetail>(`/api/bots/${botId}/porters/config`, config);
  return response.data;
};

/** 删除 (软删, status=disabled, 同时清空 token) */
export const deletePortersConfig = async (botId: string): Promise<void> => {
  const { delete: del } = Http.use();
  await del(`/api/bots/${botId}/porters/config`);
};

/** 查询激活状态 + token TTL */
export const getPortersStatus = async (botId: string): Promise<PortersStatus> => {
  const { get } = Http.use();
  const response = await get<PortersStatus>(`/api/bots/${botId}/porters/status`);
  return response.data;
};

/** 测试连接 — 实际调一次 partition + user_read */
export const testPortersConnection = async (botId: string): Promise<PortersTestResult> => {
  const { post } = Http.use();
  const response = await post<PortersTestResult>(`/api/bots/${botId}/porters/test`, {});
  return response.data;
};

/** 查 API 调用日志 (排查用) */
export const getPortersApiLogs = async (
  botId: string,
  opts: { limit?: number; operation?: string; only_errors?: boolean } = {}
): Promise<PortersApiLog[]> => {
  const { get } = Http.use();
  const params = new URLSearchParams();
  if (opts.limit != null) params.append('limit', String(opts.limit));
  if (opts.operation) params.append('operation', opts.operation);
  if (opts.only_errors) params.append('only_errors', 'true');
  const response = await get<PortersApiLog[]>(`/api/bots/${botId}/porters/logs?${params.toString()}`);
  return response.data;
};

/** 拼出 OAuth authorize URL — 调用方应该 window.open(url, '_blank') */
export const buildPortersAuthorizeUrl = (botId: string, apiBaseUrl: string): string => {
  return `${apiBaseUrl.replace(/\/$/, '')}/api/auth/porters/authorize?bot_id=${botId}`;
};
